import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import MoneyInput from '../../common/MoneyInput';
import * as FiIcons from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { calculateRetirementProjection } from '../../utils/calculations';

const { FiTrendingUp, FiCalendar } = FiIcons;

const RetirementCalculator = () => {
  const { user } = useAuth();
  const { saveCalculation, personalInfo } = useData();
  
  const currency = personalInfo?.investment_currency || 'Currency';

  const [formData, setFormData] = useState({
    currentAge: '',
    retirementAge: '',
    currentSavings: '',
    monthlyContribution: '',
    annualReturn: '7',
    inflationRate: '3'
  });

  useEffect(() => {
    if (personalInfo) {
      setFormData(prev => ({
        ...prev,
        currentAge: personalInfo.date_of_birth ? (new Date().getFullYear() - new Date(personalInfo.date_of_birth).getFullYear()) : prev.currentAge,
        retirementAge: personalInfo.retirement_age || prev.retirement_age,
        currentSavings: personalInfo.current_investment_value || prev.current_savings,
        monthlyContribution: personalInfo.recurring_investment_amount || prev.monthly_contribution,
      }));
    }
  }, [personalInfo]);

  const [results, setResults] = useState(null);

  const handleCalculate = (e) => {
    e.preventDefault();
    const calculationResults = calculateRetirementProjection(formData);
    setResults(calculationResults);
    if (user) {
      saveCalculation({ type: 'retirement', inputs: formData, results: calculationResults });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-primary-500" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Retirement Planning</h1>
          <p className="text-xl text-secondary-300 max-w-3xl mx-auto">
            Plan for a secure retirement by calculating your future portfolio value.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-secondary-900 rounded-xl shadow-lg p-8 border border-primary-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">Enter Your Details</h2>
            <form onSubmit={handleCalculate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Current Age</label>
                  <div className="relative">
                    <input type="number" name="currentAge" value={formData.currentAge} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" required />
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Retirement Age</label>
                  <div className="relative">
                    <input type="number" name="retirementAge" value={formData.retirementAge} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" required />
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                  </div>
                </div>
              </div>

              <MoneyInput
                label="Current Savings"
                name="currentSavings"
                value={formData.currentSavings}
                onChange={handleChange}
                currency={currency}
                required
              />

              <MoneyInput
                label="Monthly Savings Contribution"
                name="monthlyContribution"
                value={formData.monthlyContribution}
                onChange={handleChange}
                currency={currency}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Expected Return (%)</label>
                  <input type="number" step="0.1" name="annualReturn" value={formData.annualReturn} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Inflation Rate (%)</label>
                  <input type="number" step="0.1" name="inflationRate" value={formData.inflationRate} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
              </div>
              <button type="submit" className="w-full bg-primary-500 hover:bg-primary-400 text-black px-6 py-4 rounded-xl font-bold transition-all shadow-lg shadow-primary-500/20">
                Generate Projection
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            {results ? (
              <>
                <div className="bg-secondary-900 rounded-xl p-8 border border-primary-500/20">
                  <h3 className="text-xl font-bold text-white mb-6">Financial Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-black p-4 rounded-xl border border-primary-500/10">
                      <p className="text-secondary-400 text-sm mb-1 uppercase tracking-widest font-black">Fund at Retirement</p>
                      <p className="text-3xl font-bold text-primary-500">{currency} {results.totalRetirementFund.toLocaleString()}</p>
                    </div>
                    <div className="bg-black p-4 rounded-xl border border-primary-500/10">
                      <p className="text-secondary-400 text-sm mb-1 uppercase tracking-widest font-black">Safe Monthly Income</p>
                      <p className="text-3xl font-bold text-primary-500">{currency} {results.monthlyIncome.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="mt-4 bg-black p-4 rounded-xl border border-primary-500/10">
                    <p className="text-secondary-400 text-sm mb-1 uppercase tracking-widest font-black">Inflation Adjusted Value</p>
                    <p className="text-2xl font-bold text-secondary-200">{currency} {results.realValue.toLocaleString()}</p>
                    <p className="text-xs text-secondary-500 mt-2">This is the purchasing power in today's {currency}.</p>
                  </div>
                </div>
                <div className="bg-secondary-900 rounded-xl p-8 border border-primary-500/20">
                  <h3 className="text-xl font-bold text-white mb-6">Portfolio Growth</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={results.chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="year" stroke="#666" />
                        <YAxis stroke="#666" tickFormatter={(v) => `${currency} ${(v / 1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#111', border: '1px solid #F0A030', color: '#fff' }}
                          formatter={(value) => [`${currency} ${value.toLocaleString()}`, 'Portfolio Value']}
                        />
                        <Line type="monotone" dataKey="value" stroke="#F0A030" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-secondary-900 rounded-xl p-12 text-center border border-primary-500/10 h-full flex flex-col justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-16 h-16 text-secondary-700 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-white mb-2">Ready to Plan?</h3>
                <p className="text-secondary-400">Fill out the form to see your wealth projection.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;